import {
  apiGet,
  apiPost,
  apiPatch,
  apiDelete,
  apiUploadFormData,
} from "./client";
import type {
  DocumentAsset,
  DocumentDocType,
  DocumentDownloadUrlResponse,
  DocumentListResponse,
  DocumentSend,
  DocumentSendChannel,
  DocumentSendListResponse,
} from "@career-ops-amplify/shared";

export type { DocumentAsset, DocumentSend, DocumentDocType };

export async function listDocuments(params?: {
  docType?: string;
  jobId?: string;
  companyId?: string;
  includeArchived?: boolean;
}): Promise<DocumentListResponse> {
  const { includeArchived, ...rest } = params ?? {};
  return apiGet<DocumentListResponse>("/api/documents", {
    ...rest,
    ...(includeArchived ? { includeArchived: true } : {}),
  });
}

export async function getDocument(
  docId: string,
): Promise<{ document: DocumentAsset }> {
  return apiGet(`/api/documents/${docId}`);
}

export async function uploadDocument(input: {
  file: File;
  docType?: DocumentDocType | string;
  isDefault?: boolean;
  linkedJobId?: string;
  linkedCompanyId?: string;
  linkedContactId?: string;
  tags?: string[];
}): Promise<{ document: DocumentAsset }> {
  const fd = new FormData();
  fd.append("file", input.file);
  if (input.docType) fd.append("docType", input.docType);
  if (input.isDefault) fd.append("isDefault", "true");
  if (input.linkedJobId) fd.append("linkedJobId", input.linkedJobId);
  if (input.linkedCompanyId) fd.append("linkedCompanyId", input.linkedCompanyId);
  if (input.linkedContactId) fd.append("linkedContactId", input.linkedContactId);
  if (input.tags?.length) fd.append("tags", JSON.stringify(input.tags));
  return apiUploadFormData("/api/documents/upload", fd);
}

export async function getDocumentDownloadUrl(
  docId: string,
): Promise<DocumentDownloadUrlResponse> {
  return apiGet(`/api/documents/${docId}/download-url`);
}

export async function updateDocument(
  docId: string,
  patch: Partial<{
    fileName: string;
    docType: string;
    tags: string[];
    isDefault: boolean;
    isActive: boolean;
    linkedJobId: string | null;
    linkedCompanyId: string | null;
    linkedContactId: string | null;
  }>,
): Promise<{ document: DocumentAsset }> {
  return apiPatch(`/api/documents/${docId}`, patch);
}

export async function archiveDocument(
  docId: string,
): Promise<{ ok: boolean }> {
  return apiDelete(`/api/documents/${docId}`);
}

export async function listDocumentSends(params?: {
  jobId?: string;
}): Promise<DocumentSendListResponse> {
  return apiGet<DocumentSendListResponse>("/api/documents/sends", params);
}

export async function getDocumentSendDownloadUrl(
  sendId: string,
  index = 0,
): Promise<DocumentDownloadUrlResponse> {
  return apiGet(`/api/documents/sends/${sendId}/download-url`, { index });
}

export async function createDocumentSend(input: {
  jobId: string;
  companyId?: string;
  contactId?: string;
  channel?: DocumentSendChannel | string;
  documentIds: string[];
  notes?: string;
}): Promise<{ send: DocumentSend }> {
  return apiPost("/api/documents/sends", input);
}
